import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { User, Heart, Calendar, ArrowLeft, Sparkles, DollarSign, Trash2, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEOHelmet, SEO_CONFIG } from '@/components/SEOHelmet';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTEREST_OPTIONS = [
  'Müzik', 'Sinema', 'Tiyatro', 'Spor', 'Yemek', 'Seyahat',
  'Kitap', 'Sanat', 'Fotoğrafçılık', 'Doğa', 'Teknoloji', 'Moda',
  'Dans', 'Yoga', 'Koşu', 'Yüzme', 'Bisiklet', 'Kamp'
];

const CUISINE_OPTIONS = [
  'Türk Mutfağı', 'İtalyan', 'Japon', 'Çin', 'Hint',
  'Meksika', 'Deniz Ürünleri', 'Vejetaryen', 'Vegan',
  'Steakhouse', 'Fast Food', 'Sokak Lezzetleri'
];

const LIFESTYLE_OPTIONS = [
  'Aktif & Sportif', 'Sakin & Huzurlu', 'Sosyal & Dışa Dönük',
  'Ev Odaklı', 'Maceraperest', 'Kültürel & Sanatsal',
  'Lüks & Konforlu', 'Minimalist & Sade', 'Doğa Sevdalısı',
  'Teknoloji Meraklısı', 'Gece Hayatı Sever', 'Erken Kalkan'
];

const SOCIAL_VALUES_OPTIONS = [
  'Muhafazakar', 'Seküler', 'Liberal', 'Geleneksel',
  'Modern', 'Dindar', 'Ateist/Agnostik', 'Maneviyatçı',
  'Feminist', 'Eşitlikçi', 'Aile Odaklı', 'Kariyer Odaklı',
  'Çevreci', 'Minimalist Yaşam', 'Toplumsal Sorumluluk'
];

export default function Profile({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteNote, setDeleteNote] = useState('');
  const [profile, setProfile] = useState({
    partner_name: user?.partner_name || '',
    my_name: '',
    my_gender: '',
    relationship_start_date: '',
    living_together: null,
    my_interests: [],
    partner_interests: [],
    favorite_cuisines: [],
    budget_preference: 'medium',
    city: '',
    favorite_locations: [],
    lifestyle: [],
    social_values: [],
    special_notes: ''
  });

  useEffect(() => {
    loadProfile();
    loadCities();
    document.title = 'Profil Ayarları - Remindora';
    
    return () => {
      document.title = 'Remindora';
    };
  }, []);

  useEffect(() => {
    if (selectedCity) {
      loadDistricts(selectedCity);
    }
  }, [selectedCity]);

  const loadCities = async () => {
    try {
      const response = await axios.get(`${API}/cities`);
      const citiesList = response.data.cities || [];
      // Ensure alphabetical sorting (backend already sorts but double-check)
      setCities(citiesList.sort((a, b) => a.localeCompare(b, 'tr')));
    } catch (error) {
      console.error('Cities load error:', error);
    }
  };

  const loadDistricts = async (city) => {
    try {
      const response = await axios.get(`${API}/cities/${encodeURIComponent(city)}/districts`);
      setDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Districts load error:', error);
      setDistricts([]);
    }
  };

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.profile) {
        const profileData = {
          partner_name: user?.partner_name || '',
          ...response.data.profile
        };
        setProfile(profileData);
        
        // Load districts if city is set
        if (profileData.city) {
          setSelectedCity(profileData.city);
        }
      }
    } catch (error) {
      console.error('Profile load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!profile.my_name || profile.my_name.trim() === '') {
      toast.error('Lütfen adınızı girin');
      return;
    }
    
    if (!profile.partner_name || profile.partner_name.trim() === '') {
      toast.error('Lütfen partner adını girin');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/auth/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local storage user
      const updatedUser = { 
        ...user, 
        partner_name: profile.partner_name,
        my_name: profile.my_name 
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success('Profil kaydedildi! Hoşgeldin emaili gönderiliyor...');
      navigate('/');
    } catch (error) {
      toast.error('Profil kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const toggleArrayItem = (array, item) => {
    if (array.includes(item)) {
      return array.filter(i => i !== item);
    } else {
      return [...array, item];
    }
  };

  const deleteAccount = async () => {
    if (!deleteReason) {
      toast.error('Lütfen bir silme sebebi seçin');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/auth/account`, {
        headers: { Authorization: `Bearer ${token}` },
        data: {
          reason: deleteReason,
          note: deleteNote
        }
      });

      // Clear all local storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      toast.success('Hesabınız kalıcı olarak silindi. Hoşça kalın!');
      setDeleteModalOpen(false);
      
      // Redirect to home after 2 seconds
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hesap silinemedi');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-blue-50/30 to-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600">Profil yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHelmet {...SEO_CONFIG.profile} />
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-blue-50/30 to-neutral-50">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-md border-b border-neutral-200 sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-neutral-900">Profil Ayarları</h1>
                <p className="text-sm text-neutral-600">AI için kişiselleştirilmiş bilgiler</p>
              </div>
            </div>
          </div>
        </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* AI Info Card */}
          <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Sparkles className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-neutral-900 mb-1">AI Asistanı İçin</h3>
                  <p className="text-sm text-neutral-600">
                    Bu bilgiler sayesinde AI asistanınız sizin için daha kişiselleştirilmiş hediye, mekan ve aktivite önerileri sunabilir.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Temel Bilgiler
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="my_name">Adınız</Label>
                  <Input
                    id="my_name"
                    placeholder="Adınız"
                    value={profile.my_name}
                    onChange={(e) => setProfile({ ...profile, my_name: e.target.value })}
                    data-testid="my-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="partner_name">Partner Adı</Label>
                  <Input
                    id="partner_name"
                    placeholder="Partner adı"
                    value={profile.partner_name}
                    onChange={(e) => setProfile({ ...profile, partner_name: e.target.value })}
                    data-testid="partner-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="relationship_start">İlişki Başlangıç Tarihi (Opsiyonel)</Label>
                <Input
                  id="relationship_start"
                  type="date"
                  value={profile.relationship_start_date}
                  onChange={(e) => setProfile({ ...profile, relationship_start_date: e.target.value })}
                  data-testid="relationship-date-input"
                />
              </div>

              <div className="space-y-3">
                <Label>Cinsiyetiniz</Label>
                <div className="flex gap-4">
                  <button
                    onClick={() => setProfile({ ...profile, my_gender: 'male' })}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                      profile.my_gender === 'male'
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300'
                    }`}
                    data-testid="gender-male"
                  >
                    👨 Erkek
                  </button>
                  <button
                    onClick={() => setProfile({ ...profile, my_gender: 'female' })}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                      profile.my_gender === 'female'
                        ? 'border-pink-600 bg-pink-50 text-pink-700'
                        : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300'
                    }`}
                    data-testid="gender-female"
                  >
                    👩 Kadın
                  </button>
                </div>
                <p className="text-xs text-neutral-500 mt-2">
                  Çiçek gönderme hatırlatmaları için kullanılır
                </p>
              </div>

              <div className="space-y-3">
                <Label>Birlikte yaşıyor musunuz?</Label>
                <div className="flex gap-4">
                  <button
                    onClick={() => setProfile({ ...profile, living_together: true })}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                      profile.living_together === true
                        ? 'border-green-600 bg-green-50 text-green-700'
                        : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300'
                    }`}
                    data-testid="living-together-yes"
                  >
                    ✅ Evet
                  </button>
                  <button
                    onClick={() => setProfile({ ...profile, living_together: false })}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                      profile.living_together === false
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300'
                    }`}
                    data-testid="living-together-no"
                  >
                    ❌ Hayır
                  </button>
                </div>
                <p className="text-xs text-neutral-500 mt-2">
                  AI önerilerinde yaşam durumunuza göre aktiviteler önerir
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Interests */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5" />
                İlgi Alanları
              </CardTitle>
              <CardDescription>AI hediye ve aktivite önerileri için</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Sizin İlgi Alanlarınız</Label>
                <div className="flex flex-wrap gap-2">
                  {INTEREST_OPTIONS.map((interest) => (
                    <button
                      key={interest}
                      onClick={() => setProfile({
                        ...profile,
                        my_interests: toggleArrayItem(profile.my_interests || [], interest)
                      })}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        (profile.my_interests || []).includes(interest)
                          ? 'bg-blue-600 text-white'
                          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                      }`}
                      data-testid={`my-interest-${interest}`}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label>Partnerinizin İlgi Alanları</Label>
                <div className="flex flex-wrap gap-2">
                  {INTEREST_OPTIONS.map((interest) => (
                    <button
                      key={interest}
                      onClick={() => setProfile({
                        ...profile,
                        partner_interests: toggleArrayItem(profile.partner_interests || [], interest)
                      })}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        (profile.partner_interests || []).includes(interest)
                          ? 'bg-pink-600 text-white'
                          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                      }`}
                      data-testid={`partner-interest-${interest}`}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lifestyle */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Aktivite Tercihleri
              </CardTitle>
              <CardDescription>AI size daha uygun aktiviteler önerebilir</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {LIFESTYLE_OPTIONS.map((lifestyle) => (
                  <button
                    key={lifestyle}
                    onClick={() => setProfile({
                      ...profile,
                      lifestyle: toggleArrayItem(profile.lifestyle || [], lifestyle)
                    })}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      (profile.lifestyle || []).includes(lifestyle)
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-md'
                        : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                    }`}
                    data-testid={`lifestyle-${lifestyle}`}
                  >
                    {lifestyle}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Social Values */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Sosyal Değerler & Yaşam Felsefesi
              </CardTitle>
              <CardDescription>AI önerilerinde değerlerinize saygılı yaklaşım</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {SOCIAL_VALUES_OPTIONS.map((value) => (
                  <button
                    key={value}
                    onClick={() => setProfile({
                      ...profile,
                      social_values: toggleArrayItem(profile.social_values || [], value)
                    })}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      (profile.social_values || []).includes(value)
                        ? 'bg-gradient-to-r from-rose-600 to-pink-600 text-white shadow-md'
                        : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                    }`}
                    data-testid={`social-value-${value}`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Tercihler
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="budget">Bütçe Tercihi</Label>
                <Select
                  value={profile.budget_preference}
                  onValueChange={(value) => setProfile({ ...profile, budget_preference: value })}
                >
                  <SelectTrigger data-testid="budget-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="economic">💰 Ekonomik (2.000-4.000 TL)</SelectItem>
                    <SelectItem value="medium">💳 Orta (5.000-10.000 TL)</SelectItem>
                    <SelectItem value="luxury">💎 Lüks (15.000+ TL)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-neutral-500 mt-1">
                  Toplam bütçe: Hediye, restoran, aktivite ve diğer harcamaları içerir
                </p>
              </div>

              <div className="space-y-3">
                <Label>Sevdiğiniz Mutfaklar</Label>
                <div className="flex flex-wrap gap-2">
                  {CUISINE_OPTIONS.map((cuisine) => (
                    <button
                      key={cuisine}
                      onClick={() => setProfile({
                        ...profile,
                        favorite_cuisines: toggleArrayItem(profile.favorite_cuisines || [], cuisine)
                      })}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        (profile.favorite_cuisines || []).includes(cuisine)
                          ? 'bg-green-600 text-white'
                          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                      }`}
                      data-testid={`cuisine-${cuisine}`}
                    >
                      {cuisine}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label>Bulunduğunuz İl</Label>
                <Select
                  value={profile.city || ''}
                  onValueChange={(value) => {
                    setProfile({ ...profile, city: value, favorite_locations: [] });
                    setSelectedCity(value);
                  }}
                >
                  <SelectTrigger data-testid="city-select">
                    <SelectValue placeholder="İl Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((city) => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {profile.city && districts.length > 0 && (
                <div className="space-y-3">
                  <Label>Favori Lokasyonlar ({profile.city})</Label>
                  <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                    {districts.map((district) => (
                      <button
                        key={district}
                        onClick={() => setProfile({
                          ...profile,
                          favorite_locations: toggleArrayItem(profile.favorite_locations || [], district)
                        })}
                        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                          (profile.favorite_locations || []).includes(district)
                            ? 'bg-purple-600 text-white'
                            : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                        }`}
                        data-testid={`location-${district}`}
                      >
                        {district}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="special_notes">Özel Notlar (Alerjiler, tercihler vb.)</Label>
                <Textarea
                  id="special_notes"
                  placeholder="Örn: Vegan, deniz ürünü alerjisi var..."
                  value={profile.special_notes}
                  onChange={(e) => setProfile({ ...profile, special_notes: e.target.value })}
                  rows={3}
                  data-testid="special-notes-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex gap-3 pt-6 border-t">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-profile-button"
            >
              {saving ? 'Kaydediliyor...' : 'Profili Kaydet'}
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              data-testid="cancel-button"
            >
              İptal
            </Button>
          </div>

          {/* Delete Account Section */}
          <Card className="mt-8 border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Tehlikeli Bölge
              </CardTitle>
              <CardDescription className="text-red-600">
                Bu işlem geri alınamaz! Hesabınız ve tüm verileriniz kalıcı olarak silinecektir.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => setDeleteModalOpen(true)}
                variant="destructive"
                className="w-full bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Hesabımı Kalıcı Olarak Sil
              </Button>
              <p className="text-xs text-red-500 mt-3 text-center">
                Silinecek veriler: Tüm özel günler, kontrol listeleri, AI sohbet geçmişi ve hatırlatıcılar
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Delete Account Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Hesabı Sil
              </DialogTitle>
              <DialogDescription>
                Hesabınızı silmeden önce, lütfen silme sebebinizi belirtin.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Silme Sebebi *</Label>
                <Select value={deleteReason} onValueChange={setDeleteReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sebep seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_satisfied">Beğenmedim</SelectItem>
                    <SelectItem value="not_meet_expectations">Beklentilerimi karşılamadı</SelectItem>
                    <SelectItem value="relationship_ended">Ayrıldık</SelectItem>
                    <SelectItem value="technical_issues">Teknik sorunlar</SelectItem>
                    <SelectItem value="privacy_concerns">Gizlilik endişeleri</SelectItem>
                    <SelectItem value="other">Diğer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Ek Not (Opsiyonel)</Label>
                <Textarea
                  value={deleteNote}
                  onChange={(e) => setDeleteNote(e.target.value)}
                  placeholder="Görüşlerinizi paylaşın..."
                  rows={3}
                  maxLength={500}
                />
                <p className="text-xs text-neutral-500">{deleteNote.length}/500</p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
                <p className="text-sm font-semibold text-red-900">⚠️ Uyarı:</p>
                <ul className="text-xs text-red-800 space-y-1 ml-4">
                  <li>• Tüm özel günleriniz silinecek</li>
                  <li>• Kontrol listeleriniz silinecek</li>
                  <li>• AI sohbet geçmişiniz silinecek</li>
                  <li>• Tüm hatırlatıcılarınız silinecek</li>
                  <li>• <strong>Bu işlem geri alınamaz!</strong></li>
                </ul>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteModalOpen(false);
                  setDeleteReason('');
                  setDeleteNote('');
                }}
                className="flex-1"
              >
                İptal
              </Button>
              <Button
                onClick={deleteAccount}
                disabled={!deleteReason}
                variant="destructive"
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Hesabı Sil
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
      <Footer />
    </div>
    </>
  );
}
